"use client";
import React, { useState } from "react";
import { FaChartLine } from "react-icons/fa";
import styles from "./FuturePlansSection.module.css";

/* ------------------------------------------------------------------
   FUTURE-PLANS
------------------------------------------------------------------- */
const groupedPlans = [
  /* ───────── 1. Data-Science & Project-Management – done first ───── */
  {
    category: "Data Science & Project Management",
    plans: [
      {
        title: "Advanced Statistics for Data Science Specialization",
        provider: "Coursera",
        plannedDate: "Q3 2025",
        description: "Statistical inference, hypothesis testing and experimental design for DS."
      },
      {
        title: "Finance & Quantitative Modeling for Analysts Specialization",
        provider: "Coursera (Wharton)",
        plannedDate: "Q3 2025",
        description: "Time-value of money, portfolio theory, and financial modelling."
      },
      {
        title: "Preparing for Google Cloud Cert: Cloud Data Engineer (PC)",
        provider: "Google Cloud",
        plannedDate: "Q4 2025",
        description: "Hands-on prep for the Professional Cloud Data Engineer certification."
      },
      {
        title: "Data Science Fundamentals with Python & SQL Specialization",
        provider: "IBM",
        plannedDate: "Q4 2025",
        description: "Core DS workflows using Python, SQL and Jupyter notebooks."
      },
      {
        title: "Microsoft Azure Data Scientist Associate (DP-100) – Exam Prep",
        provider: "Microsoft",
        plannedDate: "Q1 2026",
        description: "Design and run Azure ML workloads; DP-100 certification readiness."
      },
      {
        title: "IBM Data Analytics with Excel and R Professional Certificate",
        provider: "IBM",
        plannedDate: "Q1 2026",
        description: "Business analytics with Excel power-query and the R tidyverse."
      },
      {
        title: "DeepLearning.AI Data Engineering Professional Certificate",
        provider: "DeepLearning.AI",
        plannedDate: "Q2 2026",
        description: "Modern data-engineering patterns, pipelines and MLOps foundations."
      },
      {
        title: "Google Advanced Data Analytics Professional Certificate",
        provider: "Google",
        plannedDate: "Q2 2026",
        description: "End-to-end analytics, experimentation, and ML in Google Cloud."
      },
      {
        title: "IBM Data Engineering Professional Certificate",
        provider: "IBM",
        plannedDate: "Q3 2026",
        description: "ETL, data warehousing, and real-time streaming architectures."
      },
      {
        title: "Google Data Analytics Professional Certificate",
        provider: "Google",
        plannedDate: "Q3 2026",
        description: "Data-driven decision-making with spreadsheets, SQL and visualisation."
      }
    ]
  },

  /* ───────────── 2. AWS Certifications (unchanged content) ───────── */
  {
    category: "AWS Certifications",
    plans: [
      {
        title: "AWS Cloud Practitioner Foundation",
        provider: "Amazon Web Services",
        plannedDate: "Q1 2026",
        description: "Fundamental AWS cloud concepts, security, billing, and pricing models."
      },
      {
        title: "AWS AI Practitioner Foundation",
        provider: "Amazon Web Services",
        plannedDate: "Q1 2026",
        description: "Introduction to AI and ML services on AWS, focusing on core AI tools."
      },
      {
        title: "AWS Solutions Architect Associate",
        provider: "Amazon Web Services",
        plannedDate: "Q2 2026",
        description: "Design secure, scalable, and cost-efficient architectures on AWS."
      },
      {
        title: "AWS SysOps Administrator – Associate",
        provider: "Amazon Web Services",
        plannedDate: "Q2 2027",
        description: "Deploy, manage, and operate workloads on AWS with automation."
      },
      {
        title: "AWS Machine Learning Engineer Associate",
        provider: "Amazon Web Services",
        plannedDate: "Q3 2027",
        description: "Develop and deploy ML solutions using AWS AI and ML services."
      },
      {
        title: "AWS Data Engineer Associate",
        provider: "Amazon Web Services",
        plannedDate: "Q3 2027",
        description: "Optimize AWS data pipelines, storage, and analytics solutions."
      },
      {
        title: "AWS Machine Learning Specialty",
        provider: "Amazon Web Services",
        plannedDate: "Q1 2028",
        description: "Advanced machine-learning techniques and deep learning on AWS."
      },
      {
        title: "AWS DevOps Engineer Professional",
        provider: "Amazon Web Services",
        plannedDate: "Q2 2028",
        description: "Implement DevOps methodologies using AWS automation tools."
      }
    ]
  },

  /* ─────────── 3. DevOps & Cloud Technologies (unchanged) ────────── */
  {
    category: "DevOps & Cloud Technologies",
    plans: [
      {
        title: "ITIL 4 Foundation",
        provider: "Axelos",
        plannedDate: "Q2 2026",
        description: "Introduction to IT service-management and ITIL best-practices."
      },
      {
        title: "Docker Certified Associate (DCA)",
        provider: "Docker",
        plannedDate: "Q3 2026",
        description: "Containerization fundamentals, Docker security, and orchestration."
      },
      {
        title: "Certified Kubernetes Administrator (CKA)",
        provider: "CNCF",
        plannedDate: "Q4 2026",
        description: "Hands-on Kubernetes administration, security, and troubleshooting."
      },
      {
        title: "CloudBees Jenkins Engineer",
        provider: "CloudBees",
        plannedDate: "Q1 2027",
        description: "CI/CD pipeline automation and Jenkins optimisation techniques."
      },
      {
        title: "HashiCorp Certified: Terraform Associate",
        provider: "HashiCorp",
        plannedDate: "Q2 2027",
        description: "Infrastructure-as-Code (IaC) best-practices using Terraform."
      }
    ]
  }
];

export default function FuturePlansSection() {
  return (
    <section className={styles.futurePlansSection} id="future-plans">
      <div className={styles.heading}>
        <FaChartLine className={styles.headingIcon} />
        <h3>FUTURE PLANS</h3>
      </div>

      {groupedPlans.map((category, idx) => (
        <CategoryBlock key={idx} category={category} />
      ))}
    </section>
  );
}

function CategoryBlock({ category }) {
  const [expanded, setExpanded] = useState(false);
  const displayedPlans = expanded ? category.plans : category.plans.slice(0, 3);

  return (
    <div className={styles.categoryBlock}>
      <h4 className={styles.categoryTitle}>{category.category}</h4>

      {displayedPlans.map((plan, idx) => (
        <PlanCard key={idx} plan={plan} />
      ))}

      {category.plans.length > 3 && (
        <p
          className={styles.toggleCategory}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show Less" : "Show More"}
        </p>
      )}
    </div>
  );
}

function PlanCard({ plan }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <div className={styles.iconBox}>
          <FaChartLine />
        </div>
        <h5 className={styles.cardTitle}>{plan.title}</h5>
        <p className={styles.provider}>{plan.provider}</p>
        <p className={styles.plannedDate}>{plan.plannedDate}</p>
      </div>

      {/* Description always visible */}
      <p className={styles.description}>{plan.description}</p>
    </div>
  );
}
